// The explore panel: fold button and browse list (the panel itself), search, category tabs, colour legend.
// Controls: explore.panel, explore.search, explore.categories, explore.legend.
import { html, safeUrl } from '../dom.js';
import { placeLevel, PLACES, CATEGORIES, LISTED, UNIT_TYPES } from '../content.js';
import { icon, catIcon } from './icons.js';
import { guestsText, categoryTab, teaserText } from './layout.js';
import { destinations, rankDestinations } from './destinations.js';

export function exploreParts(c) {
  const { api, T, tx, i18n } = c;
  const body = () => document.body;
  let filter = 'all', query = '';

  /** What the page offers to look for (chrome/destinations.js, the same as "Where to?"). */
  const offered = () => destinations({ keys: api.places.keys(), values: c.v() || {}, T, tx });
  const unitRow = (t) => ({ size: tx(t.size), guests: guestsText(t, 'short', i18n) });
  /** The groups of the browse list for the chosen tab and the words `text`: [[title, items]], only those with items. */
  function groups(text = query) {
    // words typed search every place, whatever tab is open (the owner, 2026-10-29: "sauna" found nothing under Stay)
    const ql = text.trim().toLowerCase(), out = [], tab = ql ? 'all' : filter;
    const listed = (id) => LISTED.filter((k) => PLACES[k].category === id && !UNIT_TYPES[k] && placeLevel(k, c.v()) !== 'scenery').map((k) => ({ id: k, title: tx(PLACES[k].title), sub: teaserText(PLACES[k], i18n), img: PLACES[k].image })); // WS-55: scenery is not in the list
    // VIS-128: the Stay group is the unit types, then the listed places of the stay category (a map whose rooms are
    // places, with no unit types, listed none). VIS-227: words typed put the single units that match first ("12" =
    // Aurora Cabin 12, its type's photo with its number on it), then the whole types ("All Aurora Cabins") and places.
    if (tab === 'all' || tab === 'stay') {
      const one = ql ? rankDestinations(offered().filter((d) => d.kind === 'unit' && UNIT_TYPES[d.unit]), ql, 8).map((d) => ({ id: d.key, title: d.name,
        sub: T('list.unit', unitRow(UNIT_TYPES[d.unit])), badge: d.number, img: UNIT_TYPES[d.unit].image, hit: true })) : [];
      const types = Object.entries(UNIT_TYPES).map(([id, t]) => ({ id, unitType: id, words: tx(t.name), title: (ql && T('list.allOf', { name: tx(t.name) })) || tx(t.name),
        sub: T('list.unit', unitRow(t)), num: c.units() ? tx(t.numbers) : '', img: t.image }));
      out.push([tx(CATEGORIES.find((x) => x.id === 'stay')?.label) || T('list.stay'), [...one, ...types, ...listed('stay')]]);
    }
    for (const cat of CATEGORIES) {
      if (cat.id === 'stay' || (tab !== 'all' && tab !== cat.id)) continue;
      const items = listed(cat.id);
      if (items.length) out.push([tx(cat.label), items]);
    }
    const match = (it) => !ql || it.hit || (it.words || it.title).toLowerCase().includes(ql) || (it.num || '').includes(ql);
    return out.map(([title, items]) => [title, items.filter(match)]).filter(([, hits]) => hits.length);
  }
  /** The browse list for the chosen tab and the words typed. */
  function list(text = query) {
    const sections = groups(text).map(([title, hits]) => html`<section><h3>${title}</h3>${hits.map((it, i) => html`
        <button class="item" style="--d:${i * 40}ms" data-id="${it.id}" ${it.unitType ? html`data-unit="${it.unitType}"` : ''}>
          <span class="thumb">${safeUrl(it.img) ? html`<img src="${safeUrl(it.img)}" alt="" loading="lazy">` : ''}${it.badge != null ? html`<b class="no">${it.badge}</b>` : ''}</span>
          <span class="txt"><b>${it.title}</b><small>${it.sub || ''}</small></span>
          ${it.num ? html`<span class="num">${it.num}</span>` : ''}
        </button>`)}</section>`);
    return html`${sections.length ? sections : html`<p class="empty">${T(text.trim() ? 'list.empty' : 'list.none')}</p>`}`; // VIS-128: "no match" only for words typed
  }
  /** What Enter takes for the words `text`: the first item of the list, else the best of everything the page offers
   *  (a named place that is not in the list): { id, unitType? } or null. */
  function first(text) {
    const hit = groups(text).flatMap(([, hits]) => hits)[0];
    if (hit) return hit;
    const d = rankDestinations(offered(), text, 1)[0];
    return d ? { id: d.key } : null;
  }
  const draw = () => { const b = document.querySelector('#browse'); if (b) b.innerHTML = html`${list()}`; };

  // "show all of a type": frame every unit of the type and keep their numbers in view for a moment
  function frameUnits(type) {
    const keys = c.unitKeys(type);
    if (!keys.length) return; // WS-51: every unit of the type is hidden in this preset (places.hidden): nothing to frame
    const pts = keys.map((k) => api.places.get(k).anchor);
    const cx = pts.reduce((s, p) => s + p.x, 0) / pts.length, cz = pts.reduce((s, p) => s + p.z, 0) / pts.length;
    const span = Math.max(...pts.map((p) => Math.hypot(p.x - cx, p.z - cz)));
    const at = pts[0].clone(); at.set(cx, api.places.get(keys[0]).base, cz);
    api.flyTo(at, Math.max(90, span * 2.6), { elevation: 38 });
    api.select(keys[0]);
    c.flash = { keys: new Set(keys), t: 2.5 };
  }
  /** A list item chosen: a whole type is framed, a place or a single unit selected (its card; Directions on a QR page). */
  function pick(it) {
    api.tours.stop();
    if (it.unitType) frameUnits(it.unitType); else api.select(it.id, true);
    body().classList.remove('menu-open');
  }
  // folded panel: explore.collapsed, set as a visitor (the runtime remembers it per preset; without a preset it is
  // remembered under today's browser key, as before)
  const fold = (on) => {
    body().classList.toggle('explore-collapsed', on);
    const b = document.querySelector('#explore-fold');
    if (b) { b.setAttribute('aria-expanded', String(!on)); b.title = T(on ? 'explore.expand' : 'explore.collapse'); }
  };
  const setFold = (on) => {
    fold(on);
    c.put({ 'explore.collapsed': on });
    if (c.legacy) try { localStorage.setItem('mms-explore-collapsed', on ? '1' : '0'); } catch { /* errors:ignore: storage blocked */ }
  };

  const parts = {
    'explore.panel': {}, // the frame below
    'explore.search': {
      html: () => html`<form id="search" autocomplete="off">${icon('search')}<input name="q" type="search" placeholder="${T('search.placeholder')}" aria-label="${T('search.label')}"></form>`,
      wire(root) {
        const form = root.querySelector('#search');
        form.q.value = query;
        form.addEventListener('input', () => { query = form.q.value; draw(); });
        form.q.addEventListener('focus', () => { if (c.phone) body().classList.add('menu-open'); }); // VIS-230: the list comes up as a sheet
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const it = first(form.q.value); // VIS-227: the first hit of the list (what the preset hides, or keeps as scenery, is never found)
          if (it) pick(it);
          else { form.classList.add('miss'); setTimeout(() => form.classList.remove('miss'), 500); }
        });
      },
    },
    'explore.categories': {
      html: () => html`<div class="cats" role="tablist">
        <button class="cat on" role="tab" data-cat="all">${T('cats.all')}</button>
        ${CATEGORIES.map((cat) => html`<button class="cat" role="tab" data-cat="${cat.id}">${catIcon(cat.id)}<span>${categoryTab(cat, i18n)}</span></button>`)}
      </div>`,
      wire(root) { // VIS-128: tabs of a tablist, the chosen one aria-selected
        const tabs = root.querySelectorAll('.cat'), mark = () => tabs.forEach((x) => { const on = x.dataset.cat === filter; x.classList.toggle('on', on); x.setAttribute('aria-selected', on); });
        mark();
        root.querySelector('.cats').addEventListener('click', (e) => {
          const b = e.target.closest('.cat'); if (!b) return;
          filter = b.dataset.cat; mark(); draw();
          if (c.phone) body().classList.add('menu-open'); // VIS-230
        });
      },
    },
    // one dot per unit type of the package; the name is the interface's `legend.<type>`, else the type's own name
    'explore.legend': {
      html: () => html`<div class="legend">
        ${Object.entries(UNIT_TYPES).map(([id, t]) => html`<span><i class="dot ${id}"></i>${T(`legend.${id}`) || tx(t.name)}</span>`)}
      </div>`,
    },
  };

  const panel = {
    slot: 'left',
    html(kids) {
      const k = Object.fromEntries(kids.map((x) => [x.id, x.html]));
      if (!('explore.panel' in k)) return '';
      return html`<aside id="explore" class="shell">
        <div class="core">
          <div class="explore-head">
            <div class="explore-top"><span class="eyebrow">${T('explore.title')}</span>
              <button class="round-btn small" id="explore-fold" aria-expanded="true" aria-controls="browse" title="${T(c.phone ? 'ph.list.close' : 'explore.collapse')}">${icon(c.phone ? 'close' : 'fold')}</button></div><!-- round 6: collapsible panel -->
            ${k['explore.search'] || ''}
          </div>
          ${k['explore.categories'] || ''}
          <nav id="browse"></nav>
          ${k['explore.legend'] || ''}
        </div>
      </aside>`;
    },
    wire(root) {
      if (!c.has('explore.search')) query = '';
      if (!c.has('explore.categories')) filter = 'all';
      if (!root) return;
      draw();
      root.querySelector('#browse').addEventListener('click', (e) => {
        const b = e.target.closest('.item'); if (!b) return;
        pick({ id: b.dataset.id, unitType: b.dataset.unit });
      });
      root.querySelector('#explore-fold').onclick = () => (c.phone ? body().classList.remove('menu-open') : setFold(!body().classList.contains('explore-collapsed'))); // VIS-230: a phone's cross folds the list into the dock
      root.querySelector('.explore-top .eyebrow').onclick = () => { if (body().classList.contains('explore-collapsed')) setFold(false); };
    },
  };
  // a phone held sideways: the "Explore" button opens the panel as a sheet; it comes and goes with the panel (VIS-230:
  // an upright phone has the dock instead)
  const mobile = {
    key: () => c.has('explore.panel') && !c.phone,
    html: () => (c.has('explore.panel') && !c.phone ? html`<button class="mobile-explore pill-btn shell-btn" id="m-explore">${icon('menu')}<span>${T('explore.open')}</span></button>` : ''),
    wire(root) { if (root) root.onclick = () => body().classList.toggle('menu-open'); else body().classList.remove('menu-open'); },
  };

  return { parts, panel, mobile, list, first, draw, sync: () => fold(!!c.v('explore.collapsed')) };
}
